

import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FaEye, FaEdit } from "react-icons/fa";
import { MdDelete, MdLocalOffer } from "react-icons/md";
import { BiPlay } from "react-icons/bi";
import { IoSearch } from "react-icons/io5";
import { deleteData, fetchDataFromApi } from "../../utils/api";
import { MyContext } from "../../App";
import { Link, useNavigate } from "react-router-dom";
import "./style.css";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const OfferBannersList = () => {
  const [offerBanners, setOfferBanners] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [sortBy, setSortBy] = useState("priority");

  const context = useContext(MyContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOfferBanners();
  }, [page, searchQuery, brandFilter, isActiveFilter, sortBy]);

  // Helper function to check if URL is video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return videoExtensions.includes(extension);
  };

  const fetchOfferBanners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "12",
        ...(searchQuery && { search: searchQuery }),
        ...(brandFilter && { brand: brandFilter }),
        ...(isActiveFilter !== "" && { isActive: isActiveFilter }),
        ...(sortBy && { sortBy: sortBy }),
      });

      const response = await fetchDataFromApi(
        `/api/homeOfferBanner?${params.toString()}`
      );

      if (response) {
        setOfferBanners(response.offerBanners || []);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching offer banners:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to fetch offer banners",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;

    try {
      await deleteData(`/api/homeOfferBanner/${selectedBanner._id}`);
      context.setAlertBox({
        open: true,
        error: false,
        msg: "Offer banner deleted successfully",
      });
      setDeleteDialogOpen(false);
      fetchOfferBanners();
    } catch (error) {
      console.error("Error deleting banner:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to delete offer banner",
      });
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setBrandFilter("");
    setIsActiveFilter("");
    setSortBy("priority");
    setPage(1);
  };

  return (
    <div className="right-content w-100">
      <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
        <h5 className="mb-0">Home Offer Banners</h5>

        <div className="ml-auto d-flex align-items-center">
          <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
            <StyledBreadcrumb
              component="a"
              href="/"
              label="Dashboard"
              icon={<HomeIcon fontSize="small" />}
            />
            <StyledBreadcrumb label="Offer Banners" deleteIcon={<ExpandMoreIcon />} />
          </Breadcrumbs>

          <Link to="/homeOfferBanners/add">
            <Button className="btn-blue ml-3 pl-3 pr-3">
              <MdLocalOffer className="mr-2" /> Add Offer Banner
            </Button>
          </Link>
        </div>
      </div>

      <Box className="card shadow border-0 p-3 mt-4">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by title, brand..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <IoSearch className="mr-2" />,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Brand"
              value={brandFilter}
              onChange={(e) => {
                setBrandFilter(e.target.value);
                setPage(1);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={isActiveFilter}
                label="Status"
                onChange={(e) => {
                  setIsActiveFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="discount">Discount</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" fullWidth onClick={resetFilters}>
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Box>
 
      <Box sx={{ marginTop: '32px' }}>
        {loading ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <CircularProgress size={48} sx={{ marginBottom: '16px' }} />
            <Typography sx={{ fontSize: '1rem', color: '#757575', fontWeight: 500 }}>
              Loading...
            </Typography>
          </Box>
        ) : offerBanners.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
              borderRadius: '12px',
              border: '2px dashed #e0e0e0',
            }}
          >
            <MdLocalOffer
              size={60}
              color="#bdbdbd"
              style={{
                marginBottom: '16px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                marginTop: '16px',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#616161',
              }}
            >
              No offer banners found
            </Typography>
            <Typography
              sx={{
                color: '#9e9e9e',
                fontSize: '0.95rem',
              }}
            >
              Create your first offer banner to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {offerBanners.map((banner) => {
              const firstMedia = banner.images?.[0];
              const isVideo = isVideoUrl(firstMedia);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={banner._id}>
                  <Card
                    className="offer-banner-card"
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      height: '100%',
                      maxHeight: '420px',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-6px) scale(1.02)',
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                        borderColor: banner.isActive ? '#4caf50' : '#ff9800',
                        '& .banner-media': {
                          transform: 'scale(1.08)',
                        },
                        '& .action-buttons': {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                    }}
                  >
                    {/* Banner Media - Image or Video */}
                    <Box sx={{ position: 'relative', height: 140, overflow: 'hidden' }}>
                      {isVideo ? (
                        <>
                          <video
                            src={firstMedia}
                            className="banner-media"
                            style={{
                              width: '100%',
                              height: '140px',
                              objectFit: 'cover',
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}
                          >
                            <BiPlay size={28} color="#ffffff" />
                          </Box>
                        </>
                      ) : (
                        <CardMedia
                          component="img"
                          height="140"
                          image={firstMedia || "/placeholder.jpg"}
                          alt={banner.title}
                          className="banner-media"
                          sx={{
                            objectFit: "cover",
                            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      )}

                      {/* Media Type Badge */}
                      {banner.images?.length > 1 && (
                        <Chip
                          label={`${banner.images.length} ${banner.images.length > 1 ? 'files' : 'file'}`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(10px)',
                            color: '#ffffff',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            height: '20px',
                          }}
                        />
                      )}
                    </Box>

                    {/* Card Content */}
                    <CardContent
                      sx={{
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }}
                    >
                      {/* Title and Status Badge */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          className="banner-title"
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: '#263238',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '2.6em',
                            flex: 1,
                          }}
                        >
                          {banner.title}
                        </Typography>
                        <Chip
                          label={banner.isActive ? "Active" : "Inactive"}
                          color={banner.isActive ? "success" : "default"}
                          size="small"
                          sx={{
                            height: '22px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        />
                      </Box>

                      {/* Brand */}
                      {banner.brand && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#757575',
                            fontSize: '0.8rem',
                            marginBottom: '4px',
                          }}
                        >
                          Brand: {banner.brand}
                        </Typography>
                      )}

                      {/* Discount Chip */}
                      {banner.discount > 0 && (
                        <Chip
                          label={`${banner.discount}% OFF`}
                          color="error"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            marginBottom: '8px',
                            maxWidth: 'fit-content',
                          }}
                        />
                      )}

                      {/* Products Count */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#757575',
                          fontSize: '0.8rem',
                          marginBottom: '4px',
                          fontWeight: 500,
                        }}
                      >
                        Products: {banner.products?.length || 0}
                      </Typography>

                      {/* Priority Badge */}
                      {banner.priority > 0 && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '4px 8px',
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                            borderRadius: '6px',
                            border: '1px solid #90caf9',
                            maxWidth: 'fit-content',
                            marginBottom: '8px',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#1976d2',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                            }}
                          >
                            Priority: {banner.priority}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <Box
                        className="action-buttons"
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-around',
                          marginTop: 'auto',
                          paddingTop: '12px',
                          borderTop: '1px solid #f5f5f5',
                          gap: 1,
                          opacity: 0.7,
                          transform: 'translateY(4px)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/homeOfferBanners/details/${banner._id}`)}
                            sx={{
                              padding: '8px',
                              background: 'rgba(25, 118, 210, 0.08)',
                              color: '#1976d2',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: '#1976d2',
                                color: '#ffffff',
                                transform: 'scale(1.15)',
                              },
                            }}
                          >
                            <FaEye size={14} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/homeOfferBanners/edit/${banner._id}`)}
                            sx={{
                              justifyContent:'center',
                              alignItems:'center',
                              padding: '8px',
                              background: 'rgba(76, 175, 80, 0.08)',
                              color: '#4caf50',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: '#4caf50',
                                color: '#ffffff',
                                transform: 'scale(1.15)',
                              },
                            }}
                          >
                            <FaEdit size={14} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBanner(banner);
                              setDeleteDialogOpen(true);
                            }}
                            sx={{
                              padding: '8px',
                              background: 'rgba(244, 67, 54, 0.08)',
                              color: '#f44336',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: '#f44336',
                                color: '#ffffff',
                                transform: 'scale(1.15) rotate(10deg)',
                              },
                            }}
                          >
                            <MdDelete size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '32px',
              marginBottom: '32px',
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                },
                '& .Mui-selected': {
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the offer banner "
            {selectedBanner?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OfferBannersList;

